/**
 * Пропорциональная карта помещений для 3D-вида.
 *
 * Контуры берутся из расчётных внутренних граней комнат, поэтому карта повторяет
 * фактическую геометрию плана, а не раскладывает комнаты условными плитками.
 */
import type { ReactNode } from "react";
import { UIKit } from "@/shared/ui";
import type { Room } from "@/widgets/plan";
import { RoomMapCanvas } from "./RoomMapCanvas";

interface Props {
	rooms: Room[];
	ceilingHeights: Record<string, number | undefined>;
	selectedRoomId: string | null;
	onSelectRoom: (roomId: string) => void;
	onClose?: () => void;
	children?: ReactNode;
}

export const RoomMap3D = ({
	rooms,
	ceilingHeights,
	selectedRoomId,
	onSelectRoom,
	onClose,
	children,
}: Props) => {
	if (rooms.length === 0) return null;

	return (
		<div className="w-56 rounded-lg border border-border bg-surface/94 p-2.5 shadow-lg backdrop-blur-sm lg:w-64 lg:p-3">
			<div className="mb-1.5 flex items-center justify-between gap-2">
				<UIKit.Text
					as="span"
					className="block text-sm font-semibold text-ink"
					title="Клик по комнате показывает только её"
				>
					Помещения
				</UIKit.Text>
				{onClose && (
					<UIKit.Button
						border={false}
						aria-label="Скрыть помещения"
						title="Скрыть схему помещений"
						className="-my-1 -mr-1 flex size-7 items-center justify-center rounded-md p-0 text-base text-muted hover:bg-accent-soft hover:text-ink"
						onClick={onClose}
					>
						✕
					</UIKit.Button>
				)}
			</div>
			<div className="overflow-hidden">
				<RoomMapCanvas
					rooms={rooms}
					ceilingHeights={ceilingHeights}
					selectedRoomId={selectedRoomId}
					onSelectRoom={onSelectRoom}
				/>
			</div>
			{children}
		</div>
	);
};

/** Мобильный диалог карты помещений: вместо перекрытия небольшого экрана. */
export const RoomMapModal3D = ({
	rooms,
	ceilingHeights,
	selectedRoomId,
	onSelectRoom,
	children,
	onClose,
}: Props & { onClose: () => void }) => {
	if (rooms.length === 0) return null;

	return (
		<UIKit.Modal
			ariaLabel="Карта помещений"
			className="p-4 md:hidden"
			containerClassName="md:hidden"
			onClose={onClose}
		>
			<div className="mb-3 flex items-center justify-between">
				<UIKit.Text className="text-sm font-semibold text-ink">Помещения</UIKit.Text>
				<UIKit.Button
					type="button"
					border={false}
					className="ml-1 flex size-9 items-center justify-center rounded-md p-0 text-lg text-ink transition-colors hover:bg-accent-soft"
					onClick={onClose}
					aria-label="Закрыть"
				>
					✕
				</UIKit.Button>
			</div>
			<RoomMapCanvas
				rooms={rooms}
				ceilingHeights={ceilingHeights}
				selectedRoomId={selectedRoomId}
				onSelectRoom={onSelectRoom}
			/>
			{children}
		</UIKit.Modal>
	);
};
